import { userPermissionsSelector } from 'app/auth/store/user.selector';
import { isFeatureAllowed } from 'app/auth/utils/utils';
import { NavigationConfigChildrenType } from 'app/fuse-configs/navigationConfig';
import { FranchiseFeatureType } from 'app/main/apps/franchises/type';
import { createSelector } from 'reselect';
import { RootState } from '..';
import { NavigationBadgesState } from './navigationBadgesSlice';
import { navigationAdapter } from './navigationSlice';

export const {
  selectAll: selectNavigation,
  selectIds: selectNavigationIds,
  selectById: selectNavigationItemById,
} = navigationAdapter.getSelectors((state: RootState) => state.fuse.navigation);

const isAllowed = (item: { featureRestriction?: FranchiseFeatureType }, permissions?: FranchiseFeatureType[]) =>
  !item.featureRestriction || isFeatureAllowed(item.featureRestriction, permissions);

const hasVisibleChildren = (item: { children?: unknown[] }) => !item.children || item.children.length > 0;

/** Keep only items whose feature is allowed, recursing into children (groups/collapses). */
const filterChildrenByFeature = (
  children: NavigationConfigChildrenType[],
  permissions: FranchiseFeatureType[] | undefined,
  badges: NavigationBadgesState,
): NavigationConfigChildrenType[] =>
  children
    .filter(child => isAllowed(child, permissions))
    .map(child => withBadge(child, badges))
    .map(child =>
      child.children ? { ...child, children: filterChildrenByFeature(child.children, permissions, badges) } : child,
    )
    .filter(hasVisibleChildren);

const withBadge = <T extends { id: string; badge?: NavigationConfigChildrenType['badge'] }>(
  item: T,
  badges: NavigationBadgesState,
): T => (badges[item.id] ? { ...item, badge: badges[item.id] } : item);

export const navigationBadgesSelector = ({ fuse }: RootState) => fuse.navigationBadges;

export const selectNavigationByFeature = createSelector(
  [selectNavigation, userPermissionsSelector, navigationBadgesSelector],
  (navigation, permissions, badges) =>
    navigation
      .filter(item => isAllowed(item, permissions))
      .map(item => withBadge(item, badges))
      .map(item =>
        item.children ? { ...item, children: filterChildrenByFeature(item.children, permissions, badges) } : item,
      )
      .filter(hasVisibleChildren),
);
